class HeaderWrapper extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.header = document.querySelector('.shopify-section-header');
    this.exitTimer = null;
    this.onEnterHandler = this.onEnter.bind(this);
    this.onLeaveHandler = this.onLeave.bind(this);
    this.addEventListener('mouseenter', this.onEnterHandler);
    this.addEventListener('mouseleave', this.onLeaveHandler);
  }

  disconnectedCallback() {
    this.removeEventListener('mouseenter', this.onEnterHandler);
    this.removeEventListener('mouseleave', this.onLeaveHandler);
  }

  onEnter() {
    this.preventClose = true;
    this.header.classList.add('is-active');
    clearTimeout(this.exitTimer);
  }

  onLeave() {
    this.exitTimer = setTimeout(() => this.header.classList.remove('is-active'), 400);
  }
}

customElements.define('header-wrapper', HeaderWrapper)

customElements.define('sticky-header',
  class StickyHeader extends HeaderWrapper {
    constructor() {
      super();
      this.header = document.querySelector('.section-header');
      this.stickyType = this.getAttribute('data-sticky-type');
      this.headerBounds = {};
      this.currentScrollTop = 0;
      this.preventReveal = false;
      this.isScrolling = null;
      this.onScrollHandler = this.onScroll.bind(this);
      this.hideHeaderOnScrollUp = () => this.preventReveal = true;
      this.onResize = debounce(this.setHeaderHeight.bind(this), 100);
    }

    connectedCallback() {
      this.setHeaderHeight();
      window.addEventListener('resize', this.onResize);
      this.addEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
      window.addEventListener('scroll', this.onScrollHandler, false);
      this.createObserver();
    }

    disconnectedCallback() {
      this.removeEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
      window.removeEventListener('scroll', this.onScrollHandler);
    }

    createObserver() {
      const observer = new IntersectionObserver(([entry]) => {
        this.headerBounds = entry.intersectionRect;
        if (this.headerBounds.top === 0 && this.headerBounds.bottom === 0) {
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
          const { top, bottom } = entry.boundingClientRect;
          this.headerBounds = { top: scrollTop + top, bottom: scrollTop + bottom };
        }
        observer.disconnect();
      });

      observer.observe(this.header);
    }

    setHeaderHeight() {
      requestAnimationFrame(() => {
        document.documentElement.style.setProperty('--header-height', `${this.header.offsetHeight}px`);
      });
    }

    onScroll() {
      if (!this.headerBounds.bottom) return;
      requestAnimationFrame(() => {
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const scrolledPast = scrollTop > this.headerBounds.bottom;
        if (this.stickyType === 'on-scroll-up') {
          if (scrollTop > this.currentScrollTop && scrolledPast) {
            this.header.classList.add('scrolled-past-header');
            if (!this.preventHide) this.hide();
          } else if (scrollTop < this.currentScrollTop && scrolledPast) {
            this.header.classList.add('scrolled-past-header');
            if (!this.preventReveal) {
              this.reveal();
            } else {
              clearTimeout(this.isScrolling);
              this.isScrolling = setTimeout(() => (this.preventReveal = false), 100);
              this.hide();
            }
          } else if (scrollTop <= this.headerBounds.top) {
            this.header.classList.remove('scrolled-past-header');
            this.reset();
          }
        } else {
          if (scrolledPast) {
            this.header.classList.add('scrolled-past-header');
            if (!this.sticky()) {
              this.hide();
              setTimeout(() => this.reveal(), 100);
            }
          } else if (scrollTop <= this.headerBounds.top) {
            this.header.classList.remove('scrolled-past-header');
            this.reset();
          }
        }

        this.currentScrollTop = scrollTop;
      });
    }

    hide() {
      this.header.classList.add('shopify-section-header-hidden', 'shopify-section-header-sticky');
      document.body.classList.add('shopify-body-section-header-hidden', 'shopify-body-section-header-sticky');
      this.closeMenuItems();
      this.closeSearchModal();
      this.closeMenuDrawer();
    }

    reveal() {
      this.header.classList.add('shopify-section-header-sticky', 'animate');
      this.header.classList.remove('shopify-section-header-hidden');
      document.body.classList.add('shopify-body-section-header-sticky');
      document.body.classList.remove('shopify-body-section-header-hidden');
    }

    reset() {
      this.header.classList.remove('shopify-section-header-hidden', 'shopify-section-header-sticky', 'animate');
    }

    sticky() {
      return this.header.classList.contains('shopify-section-header-sticky') && !this.header.classList.contains('shopify-section-header-hidden') && document.body.classList.contains('shopify-body-section-header-sticky') && !document.body.classList.contains('shopify-body-section-header-hidden');
    }

    closeMenuDrawer() {
      this.menuDrawer = this.menuDrawer || this.header.querySelectorAll('menu-drawer');
      this.menuDrawer.forEach((drawer) => drawer.isOpen && drawer.close());
    }

    closeMenuItems() {
      this.menuItems = this.menuItems || this.header.querySelectorAll('header-menu, header-submenu');
      this.menuItems.forEach((menuItem) => menuItem.closeDropdown());
    }

    closeSearchModal() {
      this.searchModals = this.searchModals || this.header.querySelectorAll('predictive-search');
      this.searchModals.forEach((searchModal) => searchModal.isOpen && searchModal.close(false));
    }
  }
)

customElements.define('header-menu',
  class HeaderMenu extends HTMLElement {
    constructor() {
      super();

      this.onPointerEnter = this.onPointerEnter.bind(this);
      this.onPointerLeave = this.onPointerLeave.bind(this);
      this.onFocusIn = this.onFocusIn.bind(this);
      this.onFocusOut = this.onFocusOut.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onTouchStart = this.onTouchStart.bind(this);
      this.computePadding = this.computePadding.bind(this);
      this._isOpen = false;
      this._hoverOpenTimer = null;
      this._hoverCloseTimer = null;
      this._resizeObs = null;
      this._lastInteraction = 'pointer';
    }

    static instances = new Set();

    get header() {
      return this._header = this._header || document.querySelector('.header-wrapper')
    }
  
    get megaMenu() {
      return this._megaMenu ||= this.querySelector('mega-menu')
    }

    _groupRoot() {
      return this.closest('nav.header__inline-menu') || document;
    }

    connectedCallback() {
      HeaderMenu.instances.add(this);
      this.setAttribute('aria-haspopup', 'true');
      this.setAttribute('aria-expanded', 'false');
      this.addEventListener('pointerenter', this.onPointerEnter);
      this.addEventListener('pointerleave', this.onPointerLeave);
      this.addEventListener('focusin', this.onFocusIn);
      this.addEventListener('focusout', this.onFocusOut);
      this.addEventListener('keydown', this.onKeyDown);
      this.addEventListener('touchstart', this.onTouchStart, { passive: true });
      this.addEventListener('pointerdown', () => { this._lastInteraction = 'pointer'; }, { passive: true });
      this.addEventListener('keydown', () => { this._lastInteraction = 'keyboard'; }, { passive: true });
      queueMicrotask(this.computePadding);
      this._resizeObs = new ResizeObserver(this.computePadding);
      this._resizeObs.observe(this);
      if (this.header) this._resizeObs.observe(this.header);
      window.addEventListener('load', this.computePadding, { once: true });
    }

    disconnectedCallback() {
      HeaderMenu.instances.delete(this);
      this.removeEventListener('pointerenter', this.onPointerEnter);
      this.removeEventListener('pointerleave', this.onPointerLeave);
      this.removeEventListener('focusin', this.onFocusIn);
      this.removeEventListener('focusout', this.onFocusOut);
      this.removeEventListener('keydown', this.onKeyDown);
      this.removeEventListener('touchstart', this.onTouchStart);
      this._resizeObs?.disconnect();
      clearTimeout(this._hoverOpenTimer);
      clearTimeout(this._hoverCloseTimer);
    }

    _blurActiveDescendant() {
      const root = this.getRootNode();
      const active = root && root.activeElement;
      if (active && this.contains(active)) active.blur();
    }

    closePredictiveSearch() {
      const searchModals = this.header?.querySelectorAll('predictive-search');
      searchModals?.forEach((searchModal) => searchModal.isOpen && searchModal.close(false));
    }

    openMenu() {
      this.computePadding();
      this.closePredictiveSearch();
      if (this._isOpen) return;
      const myGroup = this._groupRoot();
      HeaderMenu.instances.forEach(inst => {
        if (inst !== this && inst._groupRoot() === myGroup) inst.closeMenu();
      });
      this._isOpen = true;
      this.classList.add('active');
      this.megaMenu?.toggleAttribute('open', true);
      this.setAttribute('aria-expanded', 'true');
    }
  
    closeMenu() {
      if (!this._isOpen) return;
      this._isOpen = false;
      this.classList.remove('active');
      this.megaMenu?.toggleAttribute('open', false);
      this.setAttribute('aria-expanded', 'false');
    }
  
    toggleMenu() {
      this._isOpen ? this.closeMenu() : this.openMenu();
    }

    onPointerEnter() {
      clearTimeout(this._hoverCloseTimer);
      clearTimeout(this._hoverOpenTimer);
      this._hoverOpenTimer = setTimeout(() => this.openMenu(), 60);
    }
  
    onPointerLeave(e) {
      clearTimeout(this._hoverOpenTimer);
      clearTimeout(this._hoverCloseTimer);
      const to = e.relatedTarget;
      const stillInZone = to instanceof Node && (this.contains(to) || this.megaMenu?.contains(to));

      if (stillInZone) return;
      this._hoverCloseTimer = setTimeout(() => {
        if (this._lastInteraction === 'pointer') {
          this._blurActiveDescendant();
          this.closeMenu();
          return;
        }
        if (!this.matches(':focus-within')) this.closeMenu();
      }, 0);
    }
  
    onFocusIn() {
      clearTimeout(this._hoverCloseTimer);
      this.openMenu();
    }
  
    onFocusOut(e) {
      const to = e.relatedTarget;
      if (to instanceof Node && (this.contains(to) || this.megaMenu?.contains(to))) {
        return;
      }
      if (!this.contains(to)) this.closeMenu();
    }
  
    onKeyDown(e) {
      if (e.key === 'Escape') {
        this.closeMenu();
        const trigger = this.querySelector('a,button,[role="button"],[tabindex]:not([tabindex="-1"])');
        trigger?.focus();
      }
    }
  
    onTouchStart(e) {
      const interactive = e.target.closest('a,button,[role="button"]');
      if (!interactive) {
        this.toggleMenu();
      }
    }

    closeDropdown() {
      this.classList.remove('active');
    }

    computePadding() {
      const header = this.header;
      if (!header) return;

      const megaMenu = this.megaMenu;

      if (megaMenu) {
        const menuRect = this.getBoundingClientRect();
        const containingBlock = megaMenu.offsetParent || this.closest('.section-header') || document.documentElement;
        const containingRect = containingBlock === document.documentElement
          ? { left: 0, width: window.innerWidth }
          : containingBlock.getBoundingClientRect();
        const left = Math.max(0, Math.floor(menuRect.left - containingRect.left));
        const popupWidth = Math.ceil(megaMenu.offsetWidth || megaMenu.getBoundingClientRect().width || containingRect.width);
        const isRightOverlapped = left + popupWidth > containingRect.width;

        this.style.setProperty('--mega-menu-left', isRightOverlapped ? 'auto' : `${left}px`);
        this.style.setProperty('--mega-menu-right', isRightOverlapped ? '0px' : 'auto');
      }
  
      const firstChild = header.firstElementChild;
      const isTopAligned =
        firstChild?.classList.contains('header--top-center') ||
        firstChild?.classList.contains('header--top-left');
  
      if (isTopAligned) {
        this.style.setProperty('--p-header-menu', 'var(--padding-bottom)');
        return;
      }

      const headerH = Math.max(0, header.offsetHeight || 0);
      const selfH = Math.max(0, this.offsetHeight || 0);
      const padding = Math.max(0, Math.floor((headerH - selfH) / 2));
      this.style.setProperty('--p-header-menu', `${padding}px`);
    }
  }
)

if (!customElements.get('mega-menu')) {
  customElements.define(
    'mega-menu',
    class MegaMenu extends HTMLElement {
      constructor() {
        super();
        this.updateHeight = this.updateHeight.bind(this);
      }

      static get observedAttributes() {
        return ['open'];
      }

      updateHeight() {
        const flyoutHeight = this.scrollHeight;

        if (flyoutHeight > 0) {
          this.style.setProperty('--flyout-height', `${flyoutHeight}px`);
        }
      }

      loadContent() {
        const template = this.querySelector('.template-mega-menu');
        if (!template || this.hasAttribute('loaded')) return;
        const content = template.content.cloneNode(true);
        template.replaceWith(content);
        this.setAttribute('loaded', 'true');
        requestAnimationFrame(this.updateHeight);
        this.dispatchEvent(new CustomEvent('mega-menu:loaded', {
          bubbles: true,
          detail: {
            menuElement: this,
            loadedAt: new Date().toISOString()
          }
        }));
      }

      attributeChangedCallback(name, oldVal, newVal) {
        if (name === 'open' && this.hasAttribute('open') && this.hasAttribute('loaded')) {
          requestAnimationFrame(this.updateHeight);
        }

        if (name === 'open' && this.hasAttribute('open') && !this.hasAttribute('loaded')) {
          const idleCallback = window.requestIdleCallback || function (cb) { setTimeout(cb, 0); };
          idleCallback(() => this.loadContent());
        }
      }
    }
  );
}

if (!customElements.get('menu-drawer')) {
  customElements.define('menu-drawer',
    class MenuDrawer extends SideDrawer {
      constructor() {
        super();
        this.moved = false;
        this.header = this.header || document.querySelector('.header-wrapper');
      }

      open(triggeredBy) {
        if (this.header) {
          this.header.classList.add('open-menu-drawer');
          document.documentElement.style.setProperty('--header-bottom-position', `${parseInt(this.header.getBoundingClientRect().bottom)}px`);
        }
        this.onBodyClickEvent = this.onBodyClickEvent || this.onBodyClick.bind(this);
        document.body.addEventListener('click', this.onBodyClickEvent);
        window.addEventListener('resize', this.onResize);
        super.open(triggeredBy);
      }

      close() {
        super.close();
        if (this.header) {
          this.header.classList.remove('open-menu-drawer');
          document.documentElement.style.removeProperty('--header-bottom-position');
        }
        document.body.removeEventListener('click', this.onBodyClickEvent);
        window.removeEventListener('resize', this.onResize);
      }

      onBodyClick(event) {
        if (!this.activeElement.contains(event.target) && !this.contains(event.target)) {
          this.close();
        }
      }

      onResize = () => {
        this.header && document.documentElement.style.setProperty('--header-bottom-position', `${parseInt(this.header.getBoundingClientRect().bottom)}px`);
      }
    }
  );
}

if (!customElements.get('menu-details')) {
  customElements.define('menu-details',
    class MenuDetails extends HTMLElement {
      constructor() {
        super();
        this.summary.addEventListener('click', this.onSummaryClick.bind(this));
        this.button.addEventListener('click', this.onButtonClick.bind(this));
      }

      get parent() {
        return this.closest('[data-menu-details-parent]')
      }

      get details() {
        return this.querySelector('details')
      }

      get summary() {
        return this.querySelector('summary')
      }

      get button() {
        return this.querySelector('button')
      }

      setMegaMenuOpen() {
        const megaMenu = this.querySelector('mega-menu');
        megaMenu?.setAttribute('open', true);
      }
  
      setMegaMenuClose() {
        const megaMenu = this.querySelector('mega-menu');
        megaMenu?.removeAttribute('open');
      }

      onSummaryClick() {
        this.setMegaMenuOpen();
        setTimeout(() => {
          this.parent.classList.add('active');
          this.classList.add('active');
          this.setAttribute('open', true);
          this.details.setAttribute('open', true);
          this.summary.setAttribute('aria-expanded', true);
        }, 100);
      }

      onButtonClick() {
        this.setMegaMenuClose();
        this.parent.classList.remove('active');
        this.classList.remove('active');
        this.removeAttribute('open');
        this.details.removeAttribute('open');
        this.summary.setAttribute('aria-expanded', false);
      }
    }
  );
}

if (!customElements.get('menu-disclosure')) {
  customElements.define('menu-disclosure',
    class MenuDisclosure extends HTMLElement {
      constructor() {
        super();
        this._open = this.details.hasAttribute('open');
        this.onSummaryClick = this.onSummaryClick.bind(this);
        this.onKeyUp = this.onKeyUp.bind(this);
      }

      get open() {
        return this._open
      }

      set open(value) {
        value = Boolean(value);
        if (value !== this._open) {
          this._open = value;
          if (value) {
            this.siblingDisclosures.forEach((disclosure) => disclosure.closeDetails());
            this.details.setAttribute('open', '');
            this.classList.add('active');
          } else {
            this.details.removeAttribute('open');
            this.classList.remove('active');
          }
        }

        this.details.setAttribute('aria-expanded', value ? 'true' : 'false');
        this.dispatchEvent(new CustomEvent('toggle', { bubbles: true, detail: { current: this } }));
      }

      get details() {
        return this._details || this.querySelector('details')
      }

      get summaryElement() {
        return this._summaryElement || this.querySelector('summary')
      }

      get contentElement() {
        return this._contentElement || this.querySelector('summary + *')
      }

      get siblingDisclosures() {
        const group = this.closest('ul');
        if (!group) return [];

        return Array.from(group.querySelectorAll('menu-disclosure')).filter((disclosure) => {
          return disclosure !== this && disclosure.closest('ul') === group;
        });
      }

      connectedCallback() {
        this.summaryElement?.addEventListener('click', this.onSummaryClick);
        this.details.addEventListener('keyup', this.onKeyUp);
      }

      disconnectedCallback() {
        this.summaryElement?.removeEventListener('click', this.onSummaryClick);
        this.details.removeEventListener('keyup', this.onKeyUp);
      }

      onSummaryClick(event) {
        event.preventDefault();
        this.toggle();
      }

      toggle(forceState = !this.open) {
        this.open = forceState;
      }

      openDetails() {
        this.open = true;
      }

      closeDetails() {
        this.open = false;
      }

      onKeyUp(event) {
        if (event.code.toUpperCase() === 'ESCAPE') {
          this.closeDetails();
        }
      }
    }
  );
}